/**
 * AssessmentQuestionnaire — patient-facing item administration.
 *
 * - Loads the instrument (items + response options) for an administration.
 * - Renders items one page/section at a time with the response options.
 * - Autosaves answered items periodically via PUT /responses.
 * - On safety_triggered (from an autosave), immediately surfaces crisis
 *   resources.
 * - Submit -> POST /submit.
 * - Optional assistive-AI chat drawer -> POST /ai/interact. If escalated,
 *   shows crisis resources. UI copy makes clear the AI does NOT interpret
 *   results.
 *
 * Accessibility: each item is a labelled radiogroup; keyboard-navigable;
 * progress is announced via aria-live.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle2,
  Send,
  Bot,
  Sparkles,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { CrisisResources } from './CrisisResources';
import {
  behavioralAssessmentApi,
  type InstrumentDetail,
  type ItemResponse,
  type ResponseOption,
} from './types';

const ITEMS_PER_PAGE = 5;
const AUTOSAVE_INTERVAL_MS = 20_000;

interface AssessmentQuestionnaireProps {
  administrationId: string;
  instrumentCode: string;
  /** Called after a successful submit (e.g. to route back to the hub). */
  onSubmitted?: (administrationId: string) => void;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
}

export const AssessmentQuestionnaire: React.FC<AssessmentQuestionnaireProps> = ({
  administrationId,
  instrumentCode,
  onSubmitted,
}) => {
  const [detail, setDetail] = useState<InstrumentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // item_number -> selected option code
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [page, setPage] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [safetyTriggered, setSafetyTriggered] = useState(false);

  const startedAtRef = useRef<number>(Date.now());
  // Tracks the answers already persisted so autosave only sends deltas cheaply.
  const lastSavedRef = useRef<string>('');

  // AI chat drawer
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatEscalated, setChatEscalated] = useState(false);

  // ── Load instrument ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    behavioralAssessmentApi
      .getInstrument(instrumentCode)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e) => {
        if (!cancelled)
          setLoadError(e instanceof Error ? e.message : 'Failed to load assessment.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [instrumentCode]);

  const sortedItems = useMemo(
    () =>
      (detail?.items ?? [])
        .slice()
        .sort((a, b) => a.display_order - b.display_order || a.item_number - b.item_number),
    [detail],
  );
  const responseOptions: ResponseOption[] = detail?.instrument.response_options ?? [];

  const totalItems = sortedItems.length;
  const answeredCount = Object.keys(answers).length;
  const progressPct = totalItems > 0 ? Math.round((answeredCount / totalItems) * 100) : 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const pageItems = sortedItems.slice(page * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE + 1000).slice(0, ITEMS_PER_PAGE);
  const isLastPage = page >= totalPages - 1;
  const allAnswered = totalItems > 0 && answeredCount === totalItems;

  // ── Autosave ───────────────────────────────────────────────────
  const buildResponses = useCallback((): ItemResponse[] => {
    return Object.entries(answers).map(([itemNumber, code]) => ({
      item_number: Number(itemNumber),
      raw_response: code, // option `code`, per contract
    }));
  }, [answers]);

  const persist = useCallback(async () => {
    if (submitted) return;
    const responses = buildResponses();
    if (responses.length === 0) return;
    const signature = JSON.stringify(responses);
    if (signature === lastSavedRef.current) return; // nothing changed
    setSaving(true);
    try {
      const res = await behavioralAssessmentApi.saveResponses(administrationId, {
        responses,
        completion_time_seconds: Math.round((Date.now() - startedAtRef.current) / 1000),
      });
      lastSavedRef.current = signature;
      if (res.safety_triggered) {
        // Immediately surface crisis resources to the patient.
        setSafetyTriggered(true);
      }
    } catch {
      // Autosave failures are non-fatal; the next tick (or submit) retries.
    } finally {
      setSaving(false);
    }
  }, [administrationId, buildResponses, submitted]);

  // Periodic autosave.
  useEffect(() => {
    if (submitted) return;
    const id = window.setInterval(() => {
      void persist();
    }, AUTOSAVE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [persist, submitted]);

  // Save when leaving a page.
  const handleSelect = useCallback((itemNumber: number, code: string) => {
    setAnswers((prev) => ({ ...prev, [itemNumber]: code }));
  }, []);

  const goToPage = useCallback(
    (next: number) => {
      void persist();
      setPage(Math.max(0, Math.min(totalPages - 1, next)));
      if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [persist, totalPages],
  );

  // ── Submit ─────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      await persist(); // flush any unsaved answers first
      const res = await behavioralAssessmentApi.submit(administrationId);
      if (res.safety_triggered) setSafetyTriggered(true);
      setSubmitted(true);
      onSubmitted?.(administrationId);
    } catch {
      // Surface a lightweight inline error via the submit button state.
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  }, [administrationId, onSubmitted, persist]);

  // ── AI chat ────────────────────────────────────────────────────
  const sendChat = useCallback(async () => {
    const message = chatInput.trim();
    if (!message || chatSending) return;
    setChatMessages((prev) => [...prev, { role: 'user', text: message }]);
    setChatInput('');
    setChatSending(true);
    try {
      const res = await behavioralAssessmentApi.aiInteract(administrationId, message);
      setChatMessages((prev) => [...prev, { role: 'assistant', text: res.reply }]);
      if (res.escalated) {
        setChatEscalated(true);
        setSafetyTriggered(true);
      }
    } catch {
      setChatMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: 'Sorry, I could not respond right now. If this is an emergency, please use the crisis resources.',
        },
      ]);
    } finally {
      setChatSending(false);
    }
  }, [administrationId, chatInput, chatSending]);

  // ── Render ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
        Loading assessment…
      </div>
    );
  }

  if (loadError || !detail) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          {loadError ?? 'Assessment unavailable.'}
        </CardContent>
      </Card>
    );
  }

  if (submitted) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        {safetyTriggered && <CrisisResources />}
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <CheckCircle2 className="h-10 w-10 text-green-600" aria-hidden="true" />
            <h2 className="text-lg font-semibold">Thank you — your responses were submitted.</h2>
            <p className="max-w-md text-sm text-muted-foreground">
              A clinician will review your results. Results are not released
              automatically; your care team will share them with you.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      {/* Crisis banner takes priority whenever safety has triggered. */}
      {safetyTriggered && <CrisisResources />}

      {/* Header + progress */}
      <Card>
        <CardHeader>
          <CardTitle>{detail.instrument.name}</CardTitle>
          <p className="text-sm text-muted-foreground">
            Please answer honestly. There are no right or wrong answers.
          </p>
        </CardHeader>
        <CardContent>
          <div className="mb-1 flex items-center justify-between text-sm">
            <span aria-live="polite">
              {answeredCount} of {totalItems} answered
            </span>
            <span className="text-muted-foreground">
              {saving ? 'Saving…' : 'Answers save automatically'}
            </span>
          </div>
          <Progress value={progressPct} aria-label={`${progressPct}% complete`} />
          <p className="mt-1 text-xs text-muted-foreground">
            Page {page + 1} of {totalPages}
          </p>
        </CardContent>
      </Card>

      {/* Items on the current page */}
      {pageItems.map((item) => (
        <Card key={item.item_number}>
          <CardContent className="py-5">
            <fieldset>
              <legend className="mb-3 text-base font-medium text-foreground">
                <span className="mr-2 text-muted-foreground">{item.item_number}.</span>
                {item.prompt}
              </legend>
              <RadioGroup
                value={answers[item.item_number] ?? ''}
                onValueChange={(code) => handleSelect(item.item_number, code)}
                className="grid gap-2 sm:grid-cols-2"
              >
                {responseOptions.map((opt) => {
                  const id = `item-${item.item_number}-${opt.code}`;
                  const selected = answers[item.item_number] === opt.code;
                  return (
                    <div
                      key={opt.code}
                      className={`flex items-center gap-3 rounded-md border p-3 transition-colors ${
                        selected ? 'border-blue-500 bg-blue-50' : 'hover:bg-muted/50'
                      }`}
                    >
                      <RadioGroupItem value={opt.code} id={id} />
                      <Label htmlFor={id} className="flex-1 cursor-pointer font-normal">
                        {opt.label}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </fieldset>
          </CardContent>
        </Card>
      ))}

      {/* Navigation */}
      <div className="flex items-center justify-between gap-3">
        <Button
          variant="outline"
          onClick={() => goToPage(page - 1)}
          disabled={page === 0}
        >
          <ChevronLeft className="mr-1 h-4 w-4" aria-hidden="true" />
          Back
        </Button>

        {isLastPage ? (
          <Button onClick={() => void handleSubmit()} disabled={submitting || !allAnswered}>
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            Submit assessment
          </Button>
        ) : (
          <Button onClick={() => goToPage(page + 1)}>
            Next
            <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </div>

      {!allAnswered && isLastPage && (
        <p className="text-center text-xs text-muted-foreground">
          Please answer all items before submitting.
        </p>
      )}

      {/* AI assist launcher */}
      <div className="flex justify-center pt-2">
        <Button variant="ghost" size="sm" onClick={() => setChatOpen(true)}>
          <Sparkles className="mr-2 h-4 w-4" aria-hidden="true" />
          Need help understanding a question?
        </Button>
      </div>

      {/* AI chat drawer */}
      <Sheet open={chatOpen} onOpenChange={setChatOpen}>
        <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5" aria-hidden="true" />
              Assistive helper
            </SheetTitle>
            <SheetDescription>
              This helper can clarify what a question means. It does{' '}
              <strong>not</strong> interpret your answers or your results, and it
              is not a substitute for a clinician.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {chatEscalated && <CrisisResources title="It sounds like you may need support right now" />}
            {chatMessages.length === 0 && !chatEscalated && (
              <p className="text-sm text-muted-foreground">
                Ask a question like “What does this item mean?”
              </p>
            )}
            {chatMessages.map((m, i) => (
              <div
                key={i}
                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                  m.role === 'user'
                    ? 'ml-auto bg-blue-600 text-white'
                    : 'mr-auto bg-muted text-foreground'
                }`}
              >
                {m.text}
              </div>
            ))}
            {chatSending && (
              <div className="mr-auto flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Thinking…
              </div>
            )}
          </div>

          <div className="border-t p-4">
            <form
              className="flex items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void sendChat();
              }}
            >
              <Textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void sendChat();
                  }
                }}
                placeholder="Type your question…"
                rows={2}
                className="resize-none"
                aria-label="Message to the assistive helper"
              />
              <Button
                type="submit"
                size="icon"
                disabled={chatSending || !chatInput.trim()}
                aria-label="Send message"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
              </Button>
            </form>
            <button
              type="button"
              onClick={() => setChatOpen(false)}
              className="mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" aria-hidden="true" />
              Close helper
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
